import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, CreditCard, Download, CheckCircle2, FileSignature, Info, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CREDITOR = {
  name: 'Kanzlei Nabhan Gäbler & Partner',
  address: 'Bundeskanzlerplatz 2D, D-53113 Bonn',
  creditorId: 'DE98ZZZ09999999999',
};

const Sepa = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    accountHolder: '',
    street: '',
    zip: '',
    city: '',
    iban: '',
    bic: '',
    bankName: '',
    mandateRef: '',
    paymentType: 'wiederkehrend',
    signedDate: new Date().toISOString().slice(0, 10),
    place: 'Bonn',
    email: '',
    phone: '',
    confirm: false,
    agb: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mandateId, setMandateId] = useState('');
  const { toast } = useToast();

  const update = (k, v) => setForm({ ...form, [k]: v });

  const formatIban = (val) =>
    val.replace(/\s/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.confirm) {
      toast({ title: t('sepa.toast.notice'), description: t('sepa.toast.confirm_required'), variant: 'destructive' });
      return;
    }
    if (!form.agb) {
      toast({ title: t('sepa.toast.notice'), description: t('sepa.toast.agb_required'), variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        accountHolder: form.accountHolder.trim(),
        street: form.street.trim(),
        zip: form.zip.trim(),
        city: form.city.trim(),
        iban: form.iban.replace(/\s/g, '').toUpperCase(),
        bic: (form.bic || '').trim(),
        bankName: (form.bankName || '').trim(),
        mandateRef: (form.mandateRef || '').trim(),
        paymentType: form.paymentType,
        signedDate: form.signedDate,
        place: form.place.trim() || 'Bonn',
        email: form.email.trim() || null,
        phone: (form.phone || '').trim(),
        confirm: form.confirm,
        agb: form.agb,
      };
      const { data } = await axios.post(`${API}/contact/sepa-mandat`, payload);
      setMandateId(data.mandate_id || '');
      toast({
        title: t('sepa.toast.success_title'),
        description: data.autoreply_sent
          ? t('sepa.toast.success_description_with_email')
          : t('sepa.toast.success_description'),
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = t('sepa.toast.error_message_default');
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = `${detail[0].loc?.slice(-1)?.[0] || 'Eingabe'}: ${detail[0].msg}`;
      toast({ title: t('sepa.toast.error_title'), description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass =
    'w-full px-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm';
  const labelClass = 'block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2';

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={t('sepa.seo.title')}
        description={t('sepa.seo.description')}
        path="/sepa-lastschrift"
        noindex
      />
      <Header />
      <main className="pt-[150px]">
        <section className="bg-[#1B3A5C] text-white py-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-4">
              <span className="w-12 h-px bg-[#b8893d]"></span>
              {t('sepa.hero.subtitle')}
            </div>
            <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-4">{t('sepa.hero.title')}</h1>
            <p className="text-white/80 leading-relaxed max-w-3xl">
              {t('sepa.hero.description')}
            </p>
          </div>
        </section>

        {/* Info cards */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { icon: Shield, key: 'secure', title: t('sepa.info_cards.secure.title'), desc: t('sepa.info_cards.secure.description') },
                { icon: CreditCard, key: 'convenient', title: t('sepa.info_cards.convenient.title'), desc: t('sepa.info_cards.convenient.description') },
                { icon: FileSignature, key: 'revocable', title: t('sepa.info_cards.revocable.title'), desc: t('sepa.info_cards.revocable.description') },
              ].map((c) => (
                <div key={c.key} className="flex items-start gap-4 p-5 bg-[#f5f8fa] border-l-4 border-[#b8893d]">
                  <div className="w-11 h-11 bg-[#1B3A5C] rounded-sm flex items-center justify-center flex-shrink-0">
                    <c.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[#1B3A5C] font-semibold tracking-wide text-sm mb-1">{c.title}</h3>
                    <p className="text-[#34536b] text-xs leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form / Confirmation */}
        <section className="pb-20 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            {submitted ? (
              <div className="bg-[#f5f8fa] border-l-4 border-[#b8893d] p-10 lg:p-14 text-center" data-testid="sepa-success">
                <div className="w-20 h-20 bg-[#b8893d]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-[#b8893d]" />
                </div>
                <h2 className="font-serif text-3xl text-[#1B3A5C] mb-3">{t('sepa.success.title')}</h2>
                <p className="text-[#34536b] max-w-xl mx-auto mb-3">
                  {t('sepa.success.description', { accountHolder: form.accountHolder || t('sepa.success.default_salutation'), email: form.email })}
                </p>
                {mandateId && (
                  <p className="text-[#34536b]/70 text-xs font-mono mb-6" data-testid="sepa-mandate-id">
                    {t('sepa.success.mandate_id', { mandateId })}
                  </p>
                )}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => window.print()} variant="outline" className="border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white tracking-wider text-xs">
                    <Download className="mr-2 w-3.5 h-3.5" /> {t('sepa.success.print_button')}
                  </Button>
                  <Button onClick={() => (window.location.href = '/')} className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs">
                    {t('sepa.success.home_button')} <ArrowRight className="ml-2 w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-[#f5f8fa] p-8 lg:p-10 border-l-4 border-[#b8893d] rounded-sm">
                {/* Creditor info */}
                <div className="mb-8 pb-6 border-b border-gray-200">
                  <h3 className="text-[#1B3A5C] text-xs tracking-[0.2em] font-semibold mb-3">{t('sepa.form.creditor.heading')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-[#34536b] text-xs">{t('sepa.form.creditor.name_label')}</p>
                      <p className="text-[#1B3A5C] font-medium">{CREDITOR.name}</p>
                    </div>
                    <div>
                      <p className="text-[#34536b] text-xs">{t('sepa.form.creditor.address_label')}</p>
                      <p className="text-[#1B3A5C] font-medium">{CREDITOR.address}</p>
                    </div>
                    <div>
                      <p className="text-[#34536b] text-xs">{t('sepa.form.creditor.id_label')}</p>
                      <p className="text-[#1B3A5C] font-medium font-mono text-xs">{CREDITOR.creditorId}</p>
                    </div>
                  </div>
                </div>

                <h3 className="font-serif text-xl text-[#1B3A5C] mb-5">{t('sepa.form.account_holder_heading')}</h3>
                <div className="space-y-5">
                  <div>
                    <label className={labelClass}>{t('sepa.form.account_holder_name_label')}</label>
                    <input required value={form.accountHolder} onChange={(e) => update('accountHolder', e.target.value)} className={fieldClass} data-testid="sepa-holder" />
                  </div>
                    <div>
                      <label className={labelClass}>{t('sepa.form.street_label')}</label>
                      <input required value={form.street} onChange={(e) => update('street', e.target.value)} className={fieldClass} data-testid="sepa-street" placeholder={t('sepa.form.street_placeholder')} />
                    </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div>
                      <label className={labelClass}>{t('sepa.form.zip_label')}</label>
                      <input required value={form.zip} onChange={(e) => update('zip', e.target.value)} className={fieldClass} data-testid="sepa-zip" />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>{t('sepa.form.city_label')}</label>
                      <input required value={form.city} onChange={(e) => update('city', e.target.value)} className={fieldClass} data-testid="sepa-city" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t('sepa.form.email_label')}</label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        className={fieldClass}
                        data-testid="sepa-email"
                        placeholder={t('sepa.form.email_placeholder')}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>{t('sepa.form.phone_label')}</label>
                      <input
                        value={form.phone}
                        onChange={(e) => update('phone', e.target.value)}
                        className={fieldClass}
                        data-testid="sepa-phone"
                        placeholder="+49 228 30 40 640"
                      />
                    </div>
                  </div>

                  <h3 className="font-serif text-xl text-[#1B3A5C] pt-6">{t('sepa.form.bank_details_heading')}</h3>
                  <div>
                    <label className={labelClass}>{t('sepa.form.iban_label')}</label>
                    <input
                      required
                      value={form.iban}
                      onChange={(e) => update('iban', formatIban(e.target.value))}
                      placeholder={t('sepa.form.iban_placeholder')}
                      maxLength={32}
                      data-testid="sepa-iban"
                      className={`${fieldClass} font-mono tracking-wider`}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t('sepa.form.bic_label')}</label>
                      <input value={form.bic} onChange={(e) => update('bic', e.target.value.toUpperCase())} className={`${fieldClass} font-mono`} />
                    </div>
                    <div>
                      <label className={labelClass}>{t('sepa.form.bank_name_label')}</label>
                      <input value={form.bankName} onChange={(e) => update('bankName', e.target.value)} className={fieldClass} />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>{t('sepa.form.mandate_ref_label')}</label>
                    <input value={form.mandateRef} onChange={(e) => update('mandateRef', e.target.value)} className={fieldClass} />
                  </div>

                  <div>
                    <label className={labelClass}>{t('sepa.form.payment_type_label')}</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        { val: 'wiederkehrend', label: t('sepa.form.payment_type_recurring_title'), desc: t('sepa.form.payment_type_recurring_description') },
                        { val: 'einmalig', label: t('sepa.form.payment_type_once_title'), desc: t('sepa.form.payment_type_once_description') },
                      ].map((p) => (
                        <button
                          key={p.val}
                          type="button"
                          onClick={() => update('paymentType', p.val)}
                          className={`text-left p-4 border-2 rounded-sm transition-all ${
                            form.paymentType === p.val
                              ? 'border-[#b8893d] bg-[#fbf7f0]'
                              : 'border-gray-200 bg-white hover:border-[#b8893d]/40'
                          }`}
                        >
                          <p className="text-[#1B3A5C] font-semibold text-sm">{p.label}</p>
                          <p className="text-[#34536b] text-xs mt-1">{p.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Legal text */}
                  <div className="mt-6 p-5 bg-white border border-gray-200 text-xs text-[#34536b] leading-relaxed">
                    <div className="flex items-start gap-2 mb-3">
                      <Info className="w-4 h-4 text-[#b8893d] flex-shrink-0 mt-0.5" />
                      <p className="font-semibold text-[#1B3A5C]">{t('sepa.form.legal_notice_title')}</p>
                    </div>
                    <p>
                      {t('sepa.form.legal_notice_text')}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t('sepa.form.place_label')}</label>
                      <input value={form.place} onChange={(e) => update('place', e.target.value)} className={fieldClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t('sepa.form.date_label')}</label>
                      <input type="date" value={form.signedDate} onChange={(e) => update('signedDate', e.target.value)} className={fieldClass} />
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer pt-3">
                    <input
                      type="checkbox"
                      checked={form.confirm}
                      onChange={(e) => update('confirm', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#b8893d]"
                      data-testid="sepa-confirm-checkbox"
                    />
                    <span className="text-sm text-[#34536b]">
                      {t('sepa.form.confirm_checkbox_label')}
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer pt-1">
                    <input
                      type="checkbox"
                      checked={form.agb}
                      onChange={(e) => update('agb', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#b8893d]"
                      data-testid="sepa-agb-checkbox"
                    />
                    <span className="text-sm text-[#34536b]">
                      {t('sepa.form.agb_checkbox_label_before')}{' '}
                      <a href="/agb" target="_blank" rel="noopener" className="text-[#b8893d] underline">
                        {t('sepa.form.agb_checkbox_link_text')}
                      </a>{' '}
                      {t('sepa.form.agb_checkbox_label_after')}
                    </span>
                  </label>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                  <Button type="button" variant="outline" onClick={() => window.print()} className="border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white tracking-wider text-xs">
                    <Download className="mr-2 w-3.5 h-3.5" /> {t('sepa.form.print_pdf_button')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="sepa-submit-btn"
                    className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs disabled:opacity-60"
                  >
                    {submitting ? (
                      <><Loader2 className="mr-2 w-3.5 h-3.5 animate-spin" /> {t('sepa.form.submitting_button')}</>
                    ) : (
                      <>{t('sepa.form.submit_button')} <ArrowRight className="ml-2 w-3.5 h-3.5" /></>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
      <Toaster />
    </div>
  );
};

export default Sepa;
